/* corex-munequera-model.js · Reglas puras del personalizador de muñequeras (sin DOM).
   Medidas en mm con origen en la esquina superior izquierda de la zona de corte de una cara (90 × 150).
   - Corte (zona total): 90 × 150. Sangrado: 3 mm por lado (el archivo de impresión mide 96 × 156).
   - Zona segura: 80 × 140 centrada (0,5 cm por los cuatro lados).
   Diseño: { format: 'libre' | 'patron', mode: 'iguales' | 'distintas', caras: 'ambas' | 'frontal', faces }. Con «iguales»
   las caras son F y T (frontal y trasera de las dos muñequeras); con «distintas», AF, AT, BF y BT.
   Una cara: { background, layers } y, en formato «patrón», pattern (corex-munequera-pattern.js).
   Capa de imagen: { id, type: 'image', name, src, naturalWidth, naturalHeight, x, y, width, scale, rotation }.
   Capa de texto: { id, type: 'text', text, fontKey, color, fontSize, x, y, scale, rotation } (fontSize en mm).
   Las zonas físicas (A/B × frontal/trasera) y sus nombres de property los lee el webhook de corex-kpi-proxy
   (lib/print-files.js: "Muñequera A - Frontal" → A-frontal): no cambiarlos sin cambiarlo allí. */
(function (global) {
  'use strict';

  var P = global.CorexMunequeraPattern || (typeof require === 'function' ? require('./corex-munequera-pattern.js') : null);

  var CUT_W = 90;
  var CUT_H = 150;
  var BLEED = 3;
  var SAFE_MARGIN_X = 5;
  var SAFE_MARGIN_Y = 5;
  var GEO = {
    w: CUT_W, h: CUT_H, bleed: BLEED,
    cut: [0, 0, CUT_W, CUT_H],
    print: [-BLEED, -BLEED, CUT_W + BLEED, CUT_H + BLEED],
    safe: [SAFE_MARGIN_X, SAFE_MARGIN_Y, CUT_W - SAFE_MARGIN_X, CUT_H - SAFE_MARGIN_Y]
  };
  var WHITE = '#FFFFFF';
  var DEFAULT_TEXT_MM = 12;
  var LOW_DPI = 150;
  /* Un archivo que ya trae la medida de la plantilla (± esto) se coloca 1:1 sobre el corte o el sangrado. */
  var TEMPLATE_TOLERANCE_MM = 1.5;

  var TABS = {
    iguales: [
      { id: 'F', label: 'Frontal', long: 'Frontal (las dos muñequeras)', back: false },
      { id: 'T', label: 'Trasera', long: 'Trasera (las dos muñequeras)', back: true }
    ],
    distintas: [
      { id: 'AF', label: 'A · Frontal', long: 'Muñequera A · Frontal', back: false },
      { id: 'AT', label: 'A · Trasera', long: 'Muñequera A · Trasera', back: true },
      { id: 'BF', label: 'B · Frontal', long: 'Muñequera B · Frontal', back: false },
      { id: 'BT', label: 'B · Trasera', long: 'Muñequera B · Trasera', back: true }
    ]
  };
  /* Zonas físicas en el orden del pedido. prop: nombre de la property (oculta, con "_" delante). */
  var ZONES = [
    { zone: 'A-frontal', side: 'A', back: false, prop: 'Muñequera A - Frontal' },
    { zone: 'B-frontal', side: 'B', back: false, prop: 'Muñequera B - Frontal' },
    { zone: 'A-trasera', side: 'A', back: true, prop: 'Muñequera A - Trasera' },
    { zone: 'B-trasera', side: 'B', back: true, prop: 'Muñequera B - Trasera' }
  ];
  var MODE_LABEL = { iguales: 'Iguales', distintas: 'Distintas' };
  var CARAS_LABEL = { ambas: 'Frontal y trasera', frontal: 'Solo frontal' };
  var FORMAT_LABEL = { libre: 'Imagen libre', patron: 'Patrón' };

  function uid() { return 'l' + Math.random().toString(36).slice(2, 10); }
  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }
  function rad(deg) { return deg * Math.PI / 180; }
  /* Redondeo a centésimas de mm (evita 25.000000000000004 en posiciones calculadas). */
  function mm(v) { return Math.round(v * 100) / 100; }
  function rectCenter(r) { return { x: (r[0] + r[2]) / 2, y: (r[1] + r[3]) / 2 }; }
  function rectSize(r) { return { w: r[2] - r[0], h: r[3] - r[1] }; }

  /* "1F4E8C", "#1f4e8c" o "#fff" → "#1F4E8C" / "#FFFFFF"; cualquier otra cosa → null. */
  function parseHex(value) {
    var m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value || '').trim());
    if (!m) return null;
    var hex = m[1].length === 3 ? m[1].replace(/./g, function (c) { return c + c; }) : m[1];
    return '#' + hex.toUpperCase();
  }

  /* ---------- caras y modos ---------- */
  function newFace(format) {
    var f = { background: WHITE, layers: [] };
    return format === 'patron' ? Object.assign(f, { pattern: P.newPattern() }) : f;
  }
  function cloneFace(f) {
    var next = { background: f.background, layers: f.layers.map(function (l) { return Object.assign({}, l, { id: uid() }); }) };
    return f.pattern ? Object.assign(next, { pattern: P.clonePattern(f.pattern, uid) }) : next;
  }
  function faceHasContent(f) {
    return !!f && (f.layers.length > 0 || !!(f.pattern && f.pattern.images.length) || String(f.background).toUpperCase() !== WHITE);
  }
  function freshFaces(mode, format) {
    return TABS[mode].reduce(function (acc, t) { acc[t.id] = newFace(format); return acc; }, {});
  }
  function newDesign() { return { format: 'libre', mode: 'iguales', caras: 'ambas', faces: freshFaces('iguales', 'libre') }; }
  /* El formato es para todo el par: al cambiarlo se empieza de cero. */
  function withFormat(d, format) {
    if (format === d.format || !FORMAT_LABEL[format]) return d;
    return Object.assign({}, d, { format: format, faces: freshFaces(d.mode, format) });
  }
  function patchPattern(d, key, patch) {
    var f = d.faces[key];
    return f && f.pattern ? patchFace(d, key, { pattern: Object.assign({}, f.pattern, patch) }) : d;
  }
  /* Añade una imagen al patrón de una cara; si ya tiene el máximo, devuelve el diseño tal cual. */
  function addPatternImage(d, key, im) {
    var f = d.faces[key];
    if (!f || !f.pattern || f.pattern.images.length >= P.MAX_IMAGES) return d;
    return patchPattern(d, key, { images: f.pattern.images.concat([Object.assign({ id: uid() }, im)]) });
  }
  function removePatternImage(d, key, id) {
    var f = d.faces[key];
    return f && f.pattern ? patchPattern(d, key, { images: f.pattern.images.filter(function (im) { return im.id !== id; }) }) : d;
  }
  /* Pestañas visibles: con «solo frontal» no se ven (ni se imprimen) las traseras. */
  function visibleTabs(d) {
    return TABS[d.mode].filter(function (t) { return d.caras === 'ambas' || !t.back; });
  }
  /* Cambio de modo. A «distintas»: cada muñequera parte de las caras comunes. A «iguales»: se conserva la muñequera A. */
  function withMode(d, mode) {
    if (mode === d.mode || !TABS[mode]) return d;
    var f = d.faces;
    var faces = mode === 'distintas'
      ? { AF: cloneFace(f.F), BF: cloneFace(f.F), AT: cloneFace(f.T), BT: cloneFace(f.T) }
      : { F: f.AF, T: f.AT };
    return Object.assign({}, d, { mode: mode, faces: faces });
  }
  function withCaras(d, caras) { return CARAS_LABEL[caras] ? Object.assign({}, d, { caras: caras }) : d; }
  function faceKeyFor(mode, zone) {
    if (mode === 'iguales') return zone.back ? 'T' : 'F';
    return zone.side + (zone.back ? 'T' : 'F');
  }
  /* Copia la cara `from` a las demás caras visibles. */
  function copyToOthers(d, from) {
    var faces = Object.assign({}, d.faces);
    visibleTabs(d).forEach(function (t) { if (t.id !== from) faces[t.id] = cloneFace(d.faces[from]); });
    return Object.assign({}, d, { faces: faces });
  }
  /* Frontal → trasera (cada muñequera a su trasera). */
  function copyFrontToBack(d) {
    var faces = Object.assign({}, d.faces);
    if (d.mode === 'iguales') faces.T = cloneFace(d.faces.F);
    else { faces.AT = cloneFace(d.faces.AF); faces.BT = cloneFace(d.faces.BF); }
    return Object.assign({}, d, { faces: faces });
  }
  function patchFace(d, key, patch) {
    var faces = Object.assign({}, d.faces);
    faces[key] = Object.assign({}, d.faces[key], patch);
    return Object.assign({}, d, { faces: faces });
  }
  function updateLayer(d, key, id, patch) {
    return patchFace(d, key, { layers: d.faces[key].layers.map(function (l) { return l.id === id ? Object.assign({}, l, patch) : l; }) });
  }
  function addLayer(d, key, layer) { return patchFace(d, key, { layers: d.faces[key].layers.concat([layer]) }); }
  function removeLayer(d, key, id) {
    return patchFace(d, key, { layers: d.faces[key].layers.filter(function (l) { return l.id !== id; }) });
  }
  /* delta +1 = hacia delante (encima), −1 = hacia atrás. */
  function moveLayer(d, key, id, delta) {
    var layers = d.faces[key].layers.slice();
    var i = layers.findIndex(function (l) { return l.id === id; }), j = i + delta;
    if (i < 0 || j < 0 || j >= layers.length) return d;
    var tmp = layers[i];
    layers[i] = layers[j];
    layers[j] = tmp;
    return patchFace(d, key, { layers: layers });
  }

  /* ---------- capas ---------- */
  /* Tamaño de una capa en mm. measureText(l) → ancho del texto en mm a fontSize (lo da el dibujo: necesita canvas). */
  function layerSize(l, measureText) {
    if (l.type === 'text') {
      var tw = Math.max(measureText ? measureText(l) : l.text.length * l.fontSize * 0.55, l.fontSize * 0.5);
      return { w: tw * l.scale, h: l.fontSize * 1.2 * l.scale };
    }
    var w = l.width * l.scale;
    return { w: w, h: w * l.naturalHeight / l.naturalWidth };
  }
  /* Semiejes de la caja (sin girar) que ocupa una capa girada. */
  function halfExtents(size, rotation) {
    var c = Math.abs(Math.cos(rad(rotation))), s = Math.abs(Math.sin(rad(rotation)));
    return { x: (size.w * c + size.h * s) / 2, y: (size.w * s + size.h * c) / 2 };
  }
  /* Alinea una capa contra un rectángulo: left, right, top, bottom, hcenter, vcenter. Devuelve el parche { x } o { y }. */
  function alignPatch(l, size, rect, how) {
    var he = halfExtents(size, l.rotation), c = rectCenter(rect);
    switch (how) {
      case 'left': return { x: mm(rect[0] + he.x) };
      case 'right': return { x: mm(rect[2] - he.x) };
      case 'top': return { y: mm(rect[1] + he.y) };
      case 'bottom': return { y: mm(rect[3] - he.y) };
      case 'hcenter': return { x: c.x };
      case 'vcenter': return { y: c.y };
      default: return {};
    }
  }
  /* Escala para que una imagen sin girar cubra el rectángulo (cover) o quepa entera (fit). */
  function coverScale(l, rect) {
    var s = rectSize(rect), h = l.width * l.naturalHeight / l.naturalWidth;
    return Math.max(s.w / l.width, s.h / h);
  }
  function fitScale(l, rect) {
    var s = rectSize(rect), h = l.width * l.naturalHeight / l.naturalWidth;
    return Math.min(s.w / l.width, s.h / h);
  }
  function effectiveDpi(l) {
    if (l.type !== 'image') return null;
    return l.naturalWidth / (layerSize(l).w / 25.4);
  }
  var EDGE_EPS = 0.3;
  function exceeds(l, size, r) {
    var he = halfExtents(size, l.rotation);
    return l.x - he.x < r[0] - EDGE_EPS || l.x + he.x > r[2] + EDGE_EPS || l.y - he.y < r[1] - EDGE_EPS || l.y + he.y > r[3] + EDGE_EPS;
  }
  /* ¿Sale la capa de la zona segura? (marca la selección en el editor). */
  function outsideSafe(l, size) { return exceeds(l, size, GEO.safe); }
  /* Una imagen que cubre el ancho del corte hace de fondo: salirse es su función y no avisa. */
  function isBackdrop(l) { return l.type === 'image' && l.width * l.scale >= GEO.w; }
  /* Dónde queda una capa: 'fuera' (parte fuera del corte: no se verá), 'margen' (fuera de la zona segura: se imprime,
     pero puede cortarse o quedar en el pliegue) o null. */
  function edgeLevel(l, size) {
    if (isBackdrop(l)) return null;
    if (exceeds(l, size, GEO.cut)) return 'fuera';
    return exceeds(l, size, GEO.safe) ? 'margen' : null;
  }
  /* Dónde y a qué tamaño entra un archivo. info: { naturalWidth, naturalHeight } (la imagen ya recortada a su dibujo),
     box: { x, y, w, h } (ese recorte dentro de la página, en px), page: { w, h } (px de la página), pageMm: { w, h } o null
     (tamaño físico: el de un PDF o el de los ppp guardados en un PNG/JPG; null si no se sabe).
     1. Página con la medida de la plantilla (corte 90 × 150 o con sangrado 96 × 156): el dibujo queda donde está en la página.
     2. Tamaño físico conocido y cabe en el área de impresión: entra a ese tamaño, centrado en la zona segura.
     3. Si no: encaja en la zona segura. */
  function placeImage(info) {
    var ratio = info.naturalHeight / info.naturalWidth, pm = info.pageMm;
    if (pm) {
      var kx = pm.w / info.page.w, ky = pm.h / info.page.h, origin = templateOrigin(pm);
      var w = info.box.w * kx;
      if (origin) {
        return { x: mm(origin.x + (info.box.x + info.box.w / 2) * kx), y: mm(origin.y + (info.box.y + info.box.h / 2) * ky), width: mm(w), template: true };
      }
      var ps = rectSize(GEO.print);
      if (w <= ps.w + 0.01 && w * ratio <= ps.h + 0.01) {
        var c0 = rectCenter(GEO.safe);
        return { x: c0.x, y: c0.y, width: mm(w), physical: true };
      }
    }
    var s = rectSize(GEO.safe), c = rectCenter(GEO.safe);
    return { x: c.x, y: c.y, width: mm(Math.min(s.w, s.h / ratio)) };
  }
  /* Esquina superior izquierda de la página en mm de la cara si la página mide como la plantilla; si no, null. */
  function templateOrigin(pm) {
    var near = function (w, h) { return Math.abs(pm.w - w) <= TEMPLATE_TOLERANCE_MM && Math.abs(pm.h - h) <= TEMPLATE_TOLERANCE_MM; };
    if (near(CUT_W, CUT_H)) return { x: 0, y: 0 };
    if (near(CUT_W + 2 * BLEED, CUT_H + 2 * BLEED)) return { x: -BLEED, y: -BLEED };
    return null;
  }
  function imageLayer(name, src, info) {
    return Object.assign({ id: uid(), type: 'image', name: name, src: src, naturalWidth: info.naturalWidth, naturalHeight: info.naturalHeight, scale: 1, rotation: 0 }, placeImage(info));
  }
  /* Resolución guardada en la cabecera de un PNG (pHYs, antes de IDAT) o un JPG (EXIF; si no, JFIF), en ppp; null si no
     hay o si es de pantalla (< 100 ppp: 72 y 96 son valores por defecto que no dicen nada del tamaño de impresión).
     Misma lectura que densityOf de corex-textil-editor-io.js (camisetas). */
  var MIN_PRINT_DPI = 100;
  function headerDpi(bytes) {
    var d = bytes, dpi = null;
    var u16 = function (p, le) { return le ? d[p] | d[p + 1] << 8 : d[p] << 8 | d[p + 1]; };
    var u32 = function (p, le) { return le ? (d[p] | d[p + 1] << 8 | d[p + 2] << 16 | d[p + 3] << 24) >>> 0 : (d[p] << 24 | d[p + 1] << 16 | d[p + 2] << 8 | d[p + 3]) >>> 0; };
    var str = function (p, n) { return String.fromCharCode.apply(null, Array.prototype.slice.call(d, p, p + n)); };
    if (d.length > 8 && d[0] === 0x89 && str(1, 3) === 'PNG') {
      for (var p = 8; p + 8 <= d.length;) {
        var type = str(p + 4, 4);
        if (type === 'pHYs' && p + 17 <= d.length) { dpi = d[p + 16] === 1 ? u32(p + 8) * 0.0254 : null; break; }
        if (type === 'IDAT' || type === 'IEND') break;
        p += 12 + u32(p);
      }
    } else if (d.length > 4 && d[0] === 0xFF && d[1] === 0xD8) {
      var jfif = null, exif = null;
      for (var q = 2; q + 4 <= d.length && d[q] === 0xFF;) {
        var m = d[q + 1], seg = u16(q + 2), s0 = q + 4;
        if (m === 0xDA || m === 0xD9) break;
        if (m === 0xE0 && str(s0, 5) === 'JFIF\0') {
          var units = d[s0 + 7], x = u16(s0 + 8);
          jfif = units === 1 ? x : units === 2 ? x * 2.54 : null;
        }
        if (m === 0xE1 && str(s0, 6) === 'Exif\0\0') {
          var t = s0 + 6, le = str(t, 2) === 'II', ifd = t + u32(t + 4, le), n = u16(ifd, le), xres = null, unit = 2;
          for (var i = 0; i < n && ifd + 14 + i * 12 <= d.length; i++) {
            var e = ifd + 2 + i * 12, tag = u16(e, le);
            if (tag === 0x011A) { var o = t + u32(e + 8, le), den = u32(o + 4, le); xres = den ? u32(o, le) / den : null; }
            if (tag === 0x0128) unit = u16(e + 8, le);
          }
          if (xres) exif = unit === 3 ? xres * 2.54 : unit === 2 ? xres : null;
        }
        q += 2 + seg;
      }
      dpi = exif || jfif;
    }
    return dpi && dpi >= MIN_PRINT_DPI ? dpi : null;
  }
  function textLayer(fontKey) {
    var c = rectCenter(GEO.safe);
    return { id: uid(), type: 'text', text: 'Tu texto', fontKey: fontKey, color: '#141414', fontSize: DEFAULT_TEXT_MM, x: c.x, y: c.y, scale: 1, rotation: 0 };
  }

  /* ---------- pedido ---------- */
  /* Caras visibles en blanco (sin capas ni fondo de color), con su nombre largo. */
  function blankFaces(d) {
    return visibleTabs(d).filter(function (t) { return !faceHasContent(d.faces[t.id]); });
  }
  function isEmpty(d) { return blankFaces(d).length === visibleTabs(d).length; }
  /* Revisión antes del pedido: avisos de las caras visibles en el orden de las pestañas.
     [{ faceKey, face (nombre largo), layerId (null: la cara o un motivo del patrón), name, kind, dpi }]
     kind: 'blanco' | 'fuera' | 'margen' | 'resolucion'. sizeOf(l) → tamaño en mm (el del dibujo, que mide los textos). */
  function layerName(l) { return l.type === 'image' ? l.name : '«' + l.text + '»'; }
  function designIssues(d, sizeOf) {
    var out = [];
    visibleTabs(d).forEach(function (t) {
      var f = d.faces[t.id], base = { faceKey: t.id, face: t.long };
      if (!faceHasContent(f)) { out.push(Object.assign({ layerId: null, name: '', kind: 'blanco' }, base)); return; }
      f.layers.forEach(function (l) {
        var edge = edgeLevel(l, sizeOf(l)), dpi = effectiveDpi(l);
        if (edge) out.push(Object.assign({ layerId: l.id, name: layerName(l), kind: edge }, base));
        if (dpi && dpi < LOW_DPI) out.push(Object.assign({ layerId: l.id, name: layerName(l), kind: 'resolucion', dpi: Math.round(dpi) }, base));
      });
      if (f.pattern) {
        P.lowDpiImages(f.pattern, LOW_DPI).forEach(function (x) {
          out.push(Object.assign({ layerId: null, name: x.name, kind: 'resolucion', dpi: Math.round(x.dpi) }, base));
        });
      }
    });
    return out;
  }
  function issueText(i) {
    switch (i.kind) {
      case 'blanco': return 'está en blanco y saldrá sin imprimir';
      case 'fuera': return i.name + ' se sale de la muñequera: la parte de fuera no se imprimirá';
      case 'margen': return i.name + ' se sale de la zona segura: puede cortarse o quedar en el pliegue';
      case 'resolucion': return i.name + ' tiene poca resolución a este tamaño (~' + i.dpi + ' ppp): puede salir pixelada';
      default: return '';
    }
  }
  var NOTE_LABEL = { fuera: 'fuera de la muñequera', margen: 'fuera de la zona segura', resolucion: 'resolución baja', blanco: 'caras en blanco' };
  /* Constancia en el pedido (property «Revisión») de que el cliente confirmó el diseño y de los avisos que aceptó. */
  function reviewNote(issues) {
    var kinds = ['fuera', 'margen', 'resolucion', 'blanco'].filter(function (k) { return issues.some(function (i) { return i.kind === k; }); });
    return 'Confirmado por el cliente' + (kinds.length ? ' con avisos: ' + kinds.map(function (k) { return NOTE_LABEL[k]; }).join(', ') : '');
  }
  /* Archivos de impresión: uno por cara con contenido, con las zonas físicas que la usan.
     [{ faceKey, slug, zones: [ZONE] }] en el orden de ZONES. Las caras en blanco no se imprimen. */
  function printJobs(d) {
    var jobs = [];
    ZONES.forEach(function (z) {
      if (z.back && d.caras !== 'ambas') return;
      var key = faceKeyFor(d.mode, z);
      if (!faceHasContent(d.faces[key])) return;
      var job = jobs.filter(function (j) { return j.faceKey === key; })[0];
      if (!job) {
        job = { faceKey: key, slug: d.mode === 'iguales' ? (z.back ? 'trasera' : 'frontal') : z.zone, zones: [] };
        jobs.push(job);
      }
      job.zones.push(z);
    });
    return jobs;
  }
  /* B2B con mínimo (como calcetines): cfg = metafield corex.b2b_tramos { minPairs, tiers: [{ min, price }] } (precio
     final por par, IVA incluido), el mismo que usa el descuento automático de Shopify. Con menos de minPairs pares del
     diseño se paga el PVP. → { min, pairs, missing, tier, cents } o null si no hay configuración válida. */
  function b2bQuote(cfg, pairs, pvpCents) {
    if (!cfg || !Array.isArray(cfg.tiers) || !cfg.tiers.length) return null;
    var min = cfg.minPairs > 0 ? cfg.minPairs : cfg.tiers[0].min;
    var tier = pairs >= min ? cfg.tiers.filter(function (t) { return pairs >= t.min; }).sort(function (a, b) { return b.min - a.min; })[0] || null : null;
    return { min: min, pairs: pairs, missing: Math.max(0, min - pairs), tier: tier, cents: tier ? Math.round(tier.price * 100) : pvpCents };
  }
  function groupId(now, rand) { return 'munequeras-' + now + '-' + rand; }
  /* Properties de una línea. urls: { <faceKey>: url }; preview: URL del JPG; review: reviewNote (opcional). */
  function cartProperties(d, urls, preview, group, review) {
    var props = { 'Formato': FORMAT_LABEL[d.format], 'Diseño': MODE_LABEL[d.mode], 'Caras': CARAS_LABEL[d.caras] };
    if (review) props['Revisión'] = review;
    printJobs(d).forEach(function (j) {
      j.zones.forEach(function (z) { if (urls[j.faceKey]) props['_' + z.prop] = urls[j.faceKey]; });
    });
    if (preview) props['_Vista previa'] = preview;
    props._corex_group = group;
    return props;
  }

  var api = {
    GEO: GEO, TABS: TABS, ZONES: ZONES, MODE_LABEL: MODE_LABEL, CARAS_LABEL: CARAS_LABEL, FORMAT_LABEL: FORMAT_LABEL,
    withFormat: withFormat, patchPattern: patchPattern, addPatternImage: addPatternImage, removePatternImage: removePatternImage, LOW_DPI: LOW_DPI, WHITE: WHITE,
    uid: uid, clamp: clamp, rad: rad, rectCenter: rectCenter, rectSize: rectSize, parseHex: parseHex,
    newFace: newFace, cloneFace: cloneFace, faceHasContent: faceHasContent, freshFaces: freshFaces, newDesign: newDesign,
    visibleTabs: visibleTabs, withMode: withMode, withCaras: withCaras, faceKeyFor: faceKeyFor, copyToOthers: copyToOthers,
    copyFrontToBack: copyFrontToBack, patchFace: patchFace, updateLayer: updateLayer, addLayer: addLayer,
    removeLayer: removeLayer, moveLayer: moveLayer, layerSize: layerSize, halfExtents: halfExtents, alignPatch: alignPatch,
    coverScale: coverScale, fitScale: fitScale, effectiveDpi: effectiveDpi, outsideSafe: outsideSafe, edgeLevel: edgeLevel, isBackdrop: isBackdrop,
    designIssues: designIssues, issueText: issueText, reviewNote: reviewNote,
    imageLayer: imageLayer, placeImage: placeImage, templateOrigin: templateOrigin, textLayer: textLayer, headerDpi: headerDpi,
    b2bQuote: b2bQuote, blankFaces: blankFaces, isEmpty: isEmpty, printJobs: printJobs, groupId: groupId, cartProperties: cartProperties
  };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else global.CorexMunequeraModel = api;
})(this);
